import React from "react";
// MUI Components
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import Checkbox from "@material-ui/core/Checkbox";
import { remoteStatus } from "../../constants/constants";
// Other Files
import { customTheme } from "../../constants/customTheme";
import classNames from "classnames";

const useStyles = makeStyles({
  root: (props) => ({
    ...customTheme(props.theme).dialog.root,
    minWidth: "750px",
  }),
  buttonContainer: customTheme().dialog.buttonContainer,
  title: customTheme().dialog.title,
  contentContainer: {
    ...customTheme().dialog.contentContainer,
    alignItems: "center",
  },
  contentItem: customTheme().dialog.contentItem,
  buttonPrimary: (props) => customTheme(props.theme).buttonPrimary,
  buttonOutlined: (props) => customTheme(props.theme).buttonOutlined,
  studyListItem: {
    padding: "0px",
  },
  studyList: (props) => ({
    width: "auto",
    overflowY: "auto",
    ...customTheme(props.theme).customScrollBar,
  }),
});

const LoadStudyConflictDialog = (props) => {
  const classes = useStyles(props);
  const [filepath, setFilepath] = React.useState(null);

  const handleSubmit = (buttonChoice) => {
    if (props.prompt === "LOCAL") {
      props.handleGetLocalStudy(filepath);
    } else if (props.prompt === "CLOUD") {
      if (buttonChoice === "REMOTE_COPY") {
        props.handleGetRemoteStudy(props.study.id);
      } else if (buttonChoice === "LOCAL_COPY") {
        props.handleGetLocalStudy(
          props.remoteHashComparison.localCopy.study_filepath
        );
      }
    }
    props.onClose();
  };

  const dialogTitle = () => {
    if (props.prompt === "LOCAL") {
      return "Multiple Study Files Found";
    } else {
      if (props.remoteHashComparison.status === remoteStatus.localVersion) {
        return "Cloud Study Outdated";
      } else if (
        props.remoteHashComparison.status === remoteStatus.remoteVersion
      ) {
        return "Update Local Study";
      } else if (props.remoteHashComparison.status === remoteStatus.divergent) {
        return "Merge Conflict: Divergent Local and Cloud Versions";
      }
    }
  };

  const dialogText = () => {
    if (props.prompt === "LOCAL") {
      return "This study folder contains multiple files. Please select the file you would like to load. Warning: if a manually saved file (ending in .study or .study.encrypt) is selected, then the autosaved file (ending in .autosave.study or .autosave.study.encrypt) may be overwritten.";
    } else {
      if (props.remoteHashComparison.status === remoteStatus.localVersion) {
        return "A newer version of the study exists on the device. Downloading the study from the cloud will overwrite the local copy and revert to an older version. This cannot be undone.";
      } else if (
        props.remoteHashComparison.status === remoteStatus.remoteVersion
      ) {
        return "An older version of the study exists on the device. Downloading the study from the cloud will update the study to the most recent version.";
      } else if (props.remoteHashComparison.status === remoteStatus.divergent) {
        return "A different version of the study exists on the device. The local version and the cloud version were independently modified after the last sync point and cannot be merged. Downloading the study from the cloud will overwrite the local version. This cannot be undone.";
      }
    }
  };

  return (
    <Dialog
      open={props.open}
      PaperProps={{ className: classNames(classes.root) }}
    >
      {/* INFORMATION CARD */}
      <Typography
        variant="h4"
        color="textPrimary"
        align="center"
        className={classes.title}
      >
        {dialogTitle()}
      </Typography>

      <DialogContent className={classes.contentContainer}>
        <Typography
          className={classes.contentItem}
          color="textPrimary"
          align="center"
        >
          {dialogText()}
        </Typography>

        {props.prompt === "LOCAL" ? (
          <List className={classes.studyList}>
            {Object.keys(props.study[Object.keys(props.study)[0]]).map(
              (studyFilename) => {
                let studyFileInfo =
                  props.study[Object.keys(props.study)[0]][studyFilename];
                return (
                  <ListItem
                    className={classes.studyListItem}
                    key={studyFilename}
                  >
                    <ListItemIcon>
                      <Checkbox
                        color="primary"
                        onChange={() => setFilepath(studyFileInfo.filepath)}
                        checked={filepath === studyFileInfo.filepath}
                        value={studyFileInfo.filepath}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={studyFilename}
                      secondary={
                        studyFileInfo.date
                          ? `Date Modified: ${studyFileInfo.date}`
                          : null
                      }
                    />
                  </ListItem>
                );
              }
            )}
          </List>
        ) : (
          <List className={classes.studyList}>
            <ListItem className={classes.studyListItem} key={"remote"}>
              <ListItemText
                primary={`Cloud version last updated: ${
                  props.study.studyHash.length !== 0
                    ? props.study.studyHash[0][
                        Object.keys(props.study.studyHash[0])[0]
                      ]
                    : "-"
                }`}
              />
            </ListItem>
            <ListItem className={classes.studyListItem} key={"local"}>
              <ListItemText
                primary={`Local version last updated: ${
                  props.remoteHashComparison.localCopy.studyHash.length !== 0
                    ? props.remoteHashComparison.localCopy.studyHash[0][
                        Object.keys(
                          props.remoteHashComparison.localCopy.studyHash[0]
                        )[0]
                      ]
                    : "-"
                }`}
              />
            </ListItem>
          </List>
        )}
      </DialogContent>

      <DialogActions className={classes.buttonContainer}>
        <Button
          className={classes.buttonOutlined}
          onClick={() => {
            setFilepath(null);
            props.onClose();
          }}
          variant="outlined"
        >
          Cancel
        </Button>

        {(props.remoteHashComparison.status === remoteStatus.localVersion ||
          props.remoteHashComparison.status === remoteStatus.divergent) && (
          <Button
            onClick={() => handleSubmit("LOCAL_COPY")}
            variant="contained"
            color="primary"
            className={classes.buttonPrimary}
          >
            Load Local Version
          </Button>
        )}
        {props.prompt === "CLOUD" && (
          <Button
            onClick={() => handleSubmit("REMOTE_COPY")}
            variant="contained"
            color="primary"
            className={classes.buttonPrimary}
          >
            Download From Cloud
          </Button>
        )}

        {props.prompt === "LOCAL" && (
          <Button
            onClick={() => handleSubmit(null)}
            variant="contained"
            color="primary"
            className={classes.buttonPrimary}
            disabled={props.prompt === "LOCAL" && filepath === null}
          >
            Load Selected File
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LoadStudyConflictDialog;
